import React, { useState } from 'react';
import GridLayout, { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { v4 as uuidv4 } from 'uuid';
import './PageBuilder.css';
import { Keyboard, FileText, Image, HandThumbsUp } from 'react-bootstrap-icons';
import { Card, Carousel } from "react-bootstrap";

type ComponentType = 'text' | 'textarea' | 'button' | 'image' | 'imageCarousel';

interface FormComponent {
  id: string;
  type: ComponentType;
  value: string;
  style?: {
    fontSize?: number;
    fontWeight?: string;
    color?: string;
    backgroundColor?: string;
    width?: string;
    height?: string;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
  };
  placeholder?: string;
  src?: string;
}

interface ComponentProps {
  value: string;
  style?: React.CSSProperties;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  src?: string;
  images?: string[];
}

const componentsMap: Record<ComponentType, React.FC<ComponentProps>> = {
  text: ({ value, style, placeholder, onChange }) => (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder || 'Your answer'}
      style={style}
    />
  ),
  textarea: ({ value, style, placeholder, onChange }) => (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder || 'Your answer'}
      style={style}
    />
  ),
  button: ({ value, style }) => (
    <button style={style}>{value || 'Button'}</button>
  ),
  image: ({ src, style }) => (
    <img src={src || 'https://via.placeholder.com/200'} alt="img" style={{ ...style, borderRadius: '8px' }} />
  ),
  imageCarousel: ({ images, style }) => (
    <Carousel style={{ maxWidth: "100%", ...style }}>
      {(images && images.length > 0 ? images : ["https://via.placeholder.com/400x200"]).map(
        (src: string, idx: number) => (
          <Carousel.Item key={idx}>
            <img src={src} style={{ width: "100%", height: style?.height || "200px", objectFit: "cover" }} />
          </Carousel.Item>
        )
      )}
    </Carousel>
  ),
};

const AddPageBuilder: React.FC = () => {
  const [components, setComponents] = useState<FormComponent[]>([]);
  const [layout, setLayout] = useState<Layout[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const addComponent = (type: ComponentType) => {
    const id = uuidv4();
    const newComponent: FormComponent = { id, type, value: '', style: {} };
    const newLayout: Layout = { i: id, x: 0, y: Infinity, w: 10, h: 3 };
    setComponents((prev) => [...prev, newComponent]);
    setLayout((prev) => [...prev, newLayout]);
    setSelectedId(id);
  };

  const handleChange = (id: string, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setComponents((prev) =>
      prev.map((comp) => (comp.id === id ? { ...comp, value: e.target.value } : comp))
    );
  };

  const deleteComponent = (id: string) => {
    console.log("id", id)
    setComponents((prev) => prev.filter((comp) => comp.id !== id));
    setLayout((prev) => prev.filter((l) => l.i !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const updateStyle = (id: string, style: Partial<FormComponent['style']>) => {
    setComponents((prev) =>
      prev.map((comp) => (comp.id === id ? { ...comp, style: { ...comp.style, ...style } } : comp))
    );
  };

  const updatePlaceholder = (id: string, placeholder: string) => {
    setComponents((prev) =>
      prev.map((comp) => (comp.id === id ? { ...comp, placeholder } : comp))
    );
  };

  const updateImage = (id: string, file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setComponents((prev) =>
        prev.map((comp) => (comp.id === id ? { ...comp, src: reader.result as string } : comp))
      );
    };
    reader.readAsDataURL(file);
  };

  const selectedComponent = components.find((c) => c.id === selectedId);

  return (
    <div className="events-main">
      <Card>
        <div className="form-builder-wrapper">
          {/* Left sidebar */}
          <div className="component-list">
            <h3>Components</h3>
            <button onClick={() => addComponent('text')}><Keyboard /> Text</button>
            <button onClick={() => addComponent('textarea')}><FileText /> Textarea</button>
            <button onClick={() => addComponent('button')}><HandThumbsUp /> Button</button>
            <button onClick={() => addComponent('image')}><Image /> Image</button>
            <button onClick={() => addComponent("imageCarousel")}>
              🖼 Carousel
            </button>
          </div>
          {/* Right form layout */}
          <div className="form-layout">
            <GridLayout
              className="layout"
              layout={layout}
              cols={12}
              rowHeight={40}
              width={800}
              onLayoutChange={(newLayout) => {
                setLayout(newLayout)
              }}
            >
              {components.map((comp) => (
                <div
                  key={comp.id}
                  data-grid={layout.find((l) => l.i === comp.id)}
                  className={`grid-item ${selectedId === comp.id ? 'selected' : ''}`}
                  onClick={(e) => {
                    setSelectedId(comp.id)
                  }}
                >
                  <div className="component-toolbar">
                    <button
                      className="delete-btn"
                      onClick={(e) => {
                        deleteComponent(comp.id);
                      }}
                    >
                      ×
                    </button>
                  </div>
                  {React.createElement(componentsMap[comp.type], {
                    value: comp.value,
                    placeholder: comp.placeholder,
                    style: comp.style,
                    onChange: comp.type === 'text' || comp.type === 'textarea' ? (e) => {
                      handleChange(comp.id, e)
                    } : undefined,
                    src: comp.src,
                  })}
                </div>
              ))}

            </GridLayout>
            {
              components.length === 0 && (
                <div className="empty-state">
                  No components added yet. Drag and drop components from the left panel to get started.
                </div>
              )
            }
          </div>
          {/* Right-side property editor */}
          <div className="property-editor">
            {selectedComponent ? (
              <div>
                <h4>Edit Component</h4>
                {(selectedComponent.type === 'text' || selectedComponent.type === 'textarea') && (
                  <>
                    <label>Placeholder:</label>
                    <input
                      type="text"
                      value={selectedComponent.placeholder || ''}
                      onChange={(e) => updatePlaceholder(selectedComponent.id, e.target.value)}
                    />
                    <label>Font Size:</label>
                    <input
                      type="number"
                      value={selectedComponent.style?.fontSize || 14}
                      onChange={(e) => updateStyle(selectedComponent.id, { fontSize: Number(e.target.value) })}
                    />
                    <label>Font Weight:</label>
                    <select
                      value={selectedComponent.style?.fontWeight || 'normal'}
                      onChange={(e) => updateStyle(selectedComponent.id, { fontWeight: e.target.value })}
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value="bolder">Bolder</option>
                      <option value="lighter">Lighter</option>
                    </select>
                    <label>Text Color:</label>
                    <input
                      type="color"
                      value={selectedComponent.style?.color || '#000000'}
                      onChange={(e) => updateStyle(selectedComponent.id, { color: e.target.value })}
                    />
                  </>
                )}
                {selectedComponent.type === 'button' && (
                  <>
                    <label>Button Text:</label>
                    <input
                      type="text"
                      value={selectedComponent.value}
                      onChange={(e) => handleChange(selectedComponent.id, e)}
                    />

                    <label>Width (px or %):</label>
                    <input
                      type="text"
                      value={selectedComponent.style?.width || 'auto'}
                      onChange={(e) => updateStyle(selectedComponent.id, { width: e.target.value })}
                      placeholder="e.g., 200px or 50%"
                    />

                    <label>Background Color:</label>
                    <input
                      type="color"
                      value={selectedComponent.style?.backgroundColor || '#1a73e8'}
                      onChange={(e) => updateStyle(selectedComponent.id, { backgroundColor: e.target.value })}
                    />

                    <label>Text Color:</label>
                    <input
                      type="color"
                      value={selectedComponent.style?.color || '#ffffff'}
                      onChange={(e) => updateStyle(selectedComponent.id, { color: e.target.value })}
                    />

                    <label>Font Size (px):</label>
                    <input
                      type="number"
                      value={selectedComponent.style?.fontSize || 14}
                      onChange={(e) => updateStyle(selectedComponent.id, { fontSize: Number(e.target.value) })}
                    />

                    <label>Font Weight:</label>
                    <select
                      value={selectedComponent.style?.fontWeight || 'normal'}
                      onChange={(e) => updateStyle(selectedComponent.id, { fontWeight: e.target.value })}
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value="bolder">Bolder</option>
                      <option value="lighter">Lighter</option>
                    </select>

                    <label>Border Color:</label>
                    <input
                      type="color"
                      value={selectedComponent.style?.borderColor || '#1a73e8'}
                      onChange={(e) => updateStyle(selectedComponent.id, { borderColor: e.target.value })}
                    />

                    <label>Border Width (px):</label>
                    <input
                      type="number"
                      value={selectedComponent.style?.borderWidth || 1}
                      onChange={(e) => updateStyle(selectedComponent.id, { borderWidth: Number(e.target.value) })}
                    />

                    <label>Border Radius (px):</label>
                    <input
                      type="number"
                      value={selectedComponent.style?.borderRadius || 6}
                      onChange={(e) => updateStyle(selectedComponent.id, { borderRadius: Number(e.target.value) })}
                    />
                  </>
                )}
                {selectedComponent.type === 'image' && (
                  <>
                    <label>Upload Image:</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => e.target.files && updateImage(selectedComponent.id, e.target.files[0])}
                    />

                    <label>Width (px or %):</label>
                    <input
                      type="text"
                      value={selectedComponent.style?.width || '100%'}
                      onChange={(e) => updateStyle(selectedComponent.id, { width: e.target.value })}
                      placeholder="e.g., 200px or 50%"
                    />

                    <label>Height (px or auto):</label>
                    <input
                      type="text"
                      value={selectedComponent.style?.height || 'auto'}
                      onChange={(e) => updateStyle(selectedComponent.id, { height: e.target.value })}
                      placeholder="e.g., 150px or auto"
                    />
                  </>
                )}

              </div>
            ) : (
              <p>Select a component to edit</p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AddPageBuilder;
